import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Truck, AlertTriangle, Car, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const coverageTypes = [
  {
    icon: Truck,
    title: "Comprehensive Truck Insurance",
    titleRu: "Комплексное страхование грузовиков",
    desc: "Protect your business on the road with our general truck insurance. From liability to cargo coverage, we’ve got you covered for peace of mind and financial security.",
    descRu: "Защитите свой бизнес на дороге с помощью нашего общего страхования грузовиков. От ответственности до страхования грузов — мы обеспечим вам душевное спокойствие и финансовую безопасность.",
  },
  {
    icon: Shield,
    title: "Occupational Accident Insurance",
    titleRu: "Страхование от несчастных случаев на производстве",
    desc: "Stay protected while on the job. Our occupational accident insurance provides coverage for medical expenses and lost income in case of on-the-job injuries.",
    descRu: "Оставайтесь под защитой на работе. Наше страхование от несчастных случаев на производстве покрывает медицинские расходы и потерю дохода в случае травм на рабочем месте.",
  },
  {
    icon: AlertTriangle,
    title: "Physical Damage Protection",
    titleRu: "Защита от физического ущерба",
    desc: "Safeguard your trucks and trailers from unexpected damage. Our physical damage insurance covers repairs and replacements, keeping your fleet on the move.",
    descRu: "Защитите свои грузовики и прицепы от непредвиденных повреждений. Наше страхование от физического ущерба покрывает ремонт и замену, поддерживая ваш автопарк в движении.",
  },
  {
    icon: Car,
    title: "Non-Trucking Liability (NTL)",
    titleRu: "Негрузовая ответственность (NTL)",
    desc: "Get liability coverage for when your truck isn’t in service. Our NTL insurance protects you from personal liability when using your vehicle outside of work.",
    descRu: "Получите страхование ответственности, когда ваш грузовик не используется. Наше страхование NTL защищает вас от личной ответственности при использовании транспортного средства в нерабочее время.",
  },
];

const testimonials = [
  {
    company: "KGZ Trucking, IL",
    text: "AITIM Group found us the perfect coverage at a rate we couldn't find anywhere else. Their support is top-notch.",
    author: "Alex R.",
  },
  {
    company: "Swift Logistics, NY",
    text: "Switching to AITIM for our insurance was the best move for our fleet. Professional and reliable.",
    author: "Michael T.",
  },
];

const insuranceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Truck Insurance",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Comprehensive insurance solutions tailored for the trucking industry. We help you find the right coverage at the best rates."
};

const TruckInsurance = () => {
  const { language } = useLanguage();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://app-cdn.clickup.com/assets/js/forms-embed/v1.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title="Protecting Your Business & Assets | Insurance"
        description="Comprehensive insurance solutions tailored for the trucking industry. We help you find the right coverage at the best rates."
        canonical="/truck-insurance"
        schemaJson={insuranceSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0 bg-navy-950">
            <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950 opacity-50"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                Insurance
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
                Protecting Your <span className="text-primary">Business & Assets</span>
              </h1>
              <p className="text-lg lg:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed">
                Comprehensive insurance solutions tailored for the trucking industry. We help you find the right coverage at the best rates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg font-semibold w-full sm:w-auto">
                    GET A QUOTE <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:7739809090" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full">
                    <Phone className="mr-2 w-5 h-5" /> 773-980-9090
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Types Grid */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">WHAT WE COVER</div>
              <h2 className="text-3xl md:text-5xl font-black text-navy-950">
                Comprehensive<br />
                <span className="text-primary">Insurance Solutions</span>
              </h2>
              <p className="text-slate-500 mt-4 max-w-xl mx-auto text-lg">
                From the road to the jobsite, we provide complete protection for your trucking business.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {coverageTypes.map((item, i) => (
                <div
                  key={i}
                  className="group bg-white rounded-2xl p-10 border border-slate-100 shadow-sm hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/20 transition-all duration-500 relative overflow-hidden flex flex-col"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="flex items-start gap-6 relative z-10 flex-1">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors duration-300">
                      <item.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-navy-950 mb-3">
                        {language === 'ru' ? item.titleRu : item.title}
                      </h3>
                      <p className="text-slate-500 leading-relaxed">
                        {language === 'ru' ? item.descRu : item.desc}
                      </p>
                    </div>
                  </div>
                  <div className="relative z-10 mt-8 pt-6 border-t border-slate-100">
                    <Link to="/free-consultation" className="inline-flex items-center text-primary font-bold hover:text-primary/80 transition-colors uppercase tracking-wider text-sm">
                      {language === 'ru' ? 'Получить расценку' : 'Get a Quote'} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-navy-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-16">
              {/* Left Side: Heading & Nav */}
              <div className="lg:w-1/3">
                <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">TESTIMONIALS</div>
                <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                  What Our<br />Clients Say
                </h2>
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 group"
                  >
                    <ChevronLeft className="w-6 h-6 text-white group-hover:scale-110" />
                  </button>
                  <button
                    onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 group"
                  >
                    <ChevronRight className="w-6 h-6 text-white group-hover:scale-110" />
                  </button>
                </div>
              </div>

              {/* Right Side: Testimonial Card */}
              <div className="lg:w-2/3 w-full">
                <div className="bg-[#0B1221] border border-white/5 p-8 md:p-12 rounded-[2rem] relative overflow-hidden group">
                  <div className="mb-8">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="text-primary opacity-80">
                      <path d="M10 11L8 17H5L7 11H5V7H10V11ZM19 11L17 17H14L16 11H14V7H19V11Z" fill="currentColor" />
                    </svg>
                  </div>

                  <p className="text-xl md:text-3xl font-medium leading-relaxed mb-10 text-white/90 italic">
                    "{testimonials[activeTestimonial].text}"
                  </p>

                  <div className="flex items-center gap-6">
                    <div className="w-12 h-[2px] bg-primary"></div>
                    <div>
                      <div className="font-bold text-xl text-white mb-1">{testimonials[activeTestimonial].author}</div>
                      <div className="text-primary font-semibold">{testimonials[activeTestimonial].company}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Insurance Quote Form Embed */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">GET COVERED TODAY</div>
                <h2 className="text-3xl md:text-4xl font-black text-navy-950 mb-4">Insurance Quote Inquiry</h2>
                <p className="text-slate-500 max-w-lg mx-auto">
                  Please download the template from the link below before submitting your quote request:
                </p>
                <a
                  href="https://bit.ly/3ZdtgrY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-primary font-semibold hover:underline"
                >
                  https://bit.ly/3ZdtgrY
                </a>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden w-full">
                <iframe
                  src="https://forms.clickup.com/9017049670/f/8cqagj6-25797/J7CI6ISENZJOBFSJE8"
                  title="Insurance Quote Inquiry Form"
                  width="100%"
                  height="100%"
                  className="w-full clickup-embed clickup-dynamic-height"
                  onWheel={() => {}}
                  style={{ background: 'transparent', border: 'none', width: '100%', minWidth: '100%' }}
                />
                <div className="text-center py-4 border-t border-slate-100">
                  <a
                    href="https://forms.clickup.com/9017049670/f/8cqagj6-25797/J7CI6ISENZJOBFSJE8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    If form doesn't open due to technical issues please click here
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-primary py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-4xl font-black text-white mb-4">Ready to Protect Your Fleet?</h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
              Join thousands of trucking companies that trust AITIM Group for comprehensive insurance coverage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/free-consultation">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-sm px-10 h-14 text-lg font-bold">
                  Get a Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:7739809090" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white rounded-sm px-8 h-14 text-lg w-full">
                  Call 773-980-9090
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TruckInsurance;
